/* eslint-disable no-console */
import { captureException } from '@sentry/react';
import { useNavigationDrawerValues } from 'components/navigation_drawer/navigation_drawer_store';
import { useCallback, useEffect, useState } from 'react';

export type UseFullscreenOptions = {
    /** Whether to hide the sidebar when entering fullscreen */
    hideSidebar?: boolean;
};

export type UseFullscreenReturn = {
    isFullscreen: boolean;
    toggleFullscreen: () => Promise<void>;
    enterFullscreen: () => Promise<void>;
    exitFullscreen: () => Promise<void>;
};

/**
 * Hook for managing fullscreen mode and optionally hiding/showing the sidebar
 *
 * @param options - Configuration options
 * @returns Fullscreen state and control functions
 *
 * @example
 * ```tsx
 * const { isFullscreen, toggleFullscreen } = useFullscreen({ hideSidebar: true });
 *
 * return (
 *   <button onClick={toggleFullscreen}>
 *     {isFullscreen ? 'Exit Fullscreen' : 'Enter Fullscreen'}
 *   </button>
 * );
 * ```
 */
export function useFullscreen(options: UseFullscreenOptions = {}): UseFullscreenReturn {
    const { hideSidebar = true } = options;
    const [isFullscreen, setIsFullscreen] = useState(!!document.fullscreenElement);

    const { setHidden, isHidden } = useNavigationDrawerValues((state) => ({
        setHidden: state.setHidden,
        isHidden: state.isHidden,
    }));

    const enterFullscreen = useCallback(async () => {
        if (document.fullscreenElement) {
            return; // Already in fullscreen
        }

        try {
            await document.documentElement.requestFullscreen();
            setIsFullscreen(true);

            setHidden(true);
        } catch (err) {
            captureException(err, { tags: { codeLocation: 'useFullscreen - enter' } });
            console.error('Error attempting to enter fullscreen:', err);
        }
    }, [setHidden]);

    const exitFullscreen = useCallback(async () => {
        if (!document.fullscreenElement) {
            return; // Not in fullscreen
        }

        try {
            await document.exitFullscreen();
            setIsFullscreen(false);

            setHidden(false);
        } catch (err) {
            captureException(err, { tags: { codeLocation: 'useFullscreen - exit' } });
            console.error('Error attempting to exit fullscreen:', err);
        }
    }, [setHidden]);

    const toggleFullscreen = useCallback(async () => {
        if (document.fullscreenElement) {
            await exitFullscreen();
        } else {
            await enterFullscreen();
        }
    }, [enterFullscreen, exitFullscreen]);

    // Listen for fullscreen changes (e.g., ESC key, browser controls)
    useEffect(() => {
        const handleFullscreenChange = () => {
            const isCurrentlyFullscreen = !!document.fullscreenElement;

            if (isFullscreen !== isCurrentlyFullscreen) {
                setIsFullscreen(isCurrentlyFullscreen);

                if (!hideSidebar) {
                    return;
                }

                // Sync sidebar state with fullscreen state
                if (isCurrentlyFullscreen !== isHidden) {
                    // Hide sidebar when entering fullscreen and reveal when exiting
                    setHidden(isCurrentlyFullscreen);
                }
            }
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
        };
    }, [isFullscreen, isHidden, hideSidebar, setHidden]);

    return {
        isFullscreen,
        toggleFullscreen,
        enterFullscreen,
        exitFullscreen,
    };
}
